import {Companies, CompanyLogs, db, Factions, Matches, Objectives, Powerups, Superstars} from "../models/models.js";
import {
    commaSeparate, getArrayMaxByProperty,
    getCurrentGame,
    getRandomChoice,
    getRandomInt,
    objectInArray, staffRoles,
    toTitleCase,
    weightedChoice
} from "./helpers.js";
import {getGameCurrentUserCompany, getGameMainUserCompany} from "./games.js";
import {
    addCompanyCompanyLogs,
    addCompanyMessages,
    addCompanyNewsItems,
    addCompanyObjectives,
    getCompanyChampionships,
    getCompanyFacilities,
    getCompanyGame,
    getCompanyGimmickMatches,
    getCompanyMessages,
    getCompanyObjectives,
    getCompanyPowerups,
    getCompanyRosters,
    getCompanyStaff,
    getCompanyStoryChoices,
    removeCompanyObjectives
} from "../models/company.js";
import {globals, globalUser} from "./globals.js";
import {
    getCompanyLatestEvent,
    getCompanyParentEvents,
    getCompanyRosterByType,
    getCompanyRosterCapacity,
    getCompanyUnreadMessages,
    getCpuControlledCompanies,
    getRandomStaffMemberByRole
} from "./companies.js";
import {addRosterFactions, addRosterStaff, addRosterSuperstars, getRosterSuperstars} from "../models/roster.js";
import {getSuperstarChampionships, getSuperstarGimmicks, getSuperstarTagTeams} from "../models/superstar.js";
import {getEventMatches} from "../models/event.js";
import {getMatchGimmickMatch, getMatchParticipants} from "../models/match.js";
import {getRosterActiveFactions} from "./factions.js";
import {getEventMaximumMatchesAndSegments} from "./events.js";
import {
    createChangeObjectivePowerupMessage,
    createInjuryReturnPowerupMessage,
    createIntroducePowerupsMessage,
    createOwnerAppearancePowerupMessage, createRecruitmentPowerupMessage,
    createResignPowerupMessage,
    createRetirementReturnPermanentPowerupMessage,
    createRetirementReturnPowerupMessage, createStaffRecruitmentPowerupMessage,
    createStyleChangePowerupMessage
} from "./messages.js";
import {
    changeSuperstarMoraleToHappy,
    endSuperstarInjury,
    removeSuperstarFactionRelationships,
    removeSuperstarManagerRelationships,
    removeSuperstarTagTeams
} from "./superstars.js";
import {getObjectiveProperties} from "../models/objective.js";
import {createGimmick, transferTalentToRoster} from "./rosters.js";
import {CompanyLog} from "../models/companyLog.js";
import {getFactionSuperstars} from "../models/faction.js";
import {createNewsItem, generateNewsItem} from "./news.js";
import {generateStaffMember, generateSuperstar} from "./generate.js";
import {generateCompanySingleObjective} from "./objectives.js";
import {loadUser} from "./user.js";

async function getPowerupsData() {  // Previously get_powerups
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const user = loadUser();
    const powerups = await getCompanyPowerups(userCompany);
    powerups.sort((a, b) => a.cost - b.cost || a.$loki - b.$loki);
    const powerupsData = powerups.map(x => JSON.parse(JSON.stringify(x)));
    for (const x of powerupsData) {
        x['uid'] = x.$loki;
    }

    // Show WoM powerups as hidden if not unlocked
    let unlocked = false;
    if (user && globalUser.paid) {
        const userPowerups = user.unlockedPowerups;
        if (userPowerups.length) {
            unlocked = true
        }
    }

    if (!unlocked && userCompany.storyName !== 'mars') {
        const wrestlingOnMarsPowerups = [
            {'name': 'Cyborg', 'level': 1},
            {'name': 'Neural Control', 'level': 1},
            {'name': 'Neural Control+', 'level': 2}
        ];
        for (const powerup of wrestlingOnMarsPowerups) {
            let data = {};
            data['name'] = powerup.name;
            data['level'] = powerup.level;
            data['storyName'] = 'mars';
            data['unlocked'] = false;
            data['stock'] = 0;
            data['unavailable'] = true;
            powerupsData.push(data);
        }
    }
    return powerupsData;
}

async function buyPowerup({uid} = {}) {  // buy_powerup
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);

    // Get powerup
    const powerup = await Powerups.get(uid);
    if (!powerup) {
        data['error'] = 'powerup-not-found';
        return data;
    }

    // Check if enough points
    if (selectedCompany.points < powerup.cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Add stock
    powerup.stock += 1;

    // Remove points
    selectedCompany.points -= powerup.cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['stock'] = powerup.stock;

    return data;
}

async function usePowerup({uid} = {}) {
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const powerup = await Powerups.get(uid);
    return getPowerupSelections(selectedCompany, powerup);
}

async function applyPowerup({uid, selectionUid} = {}) {  // apply_powerup_view
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const powerup = await Powerups.get(uid);
    let data = await applyPowerupToSelection(selectedCompany, powerup, selectionUid);
    db.saveDatabase();
    return data;
}

async function getPowerupSelections(company, powerup) {  // get_powerup_selections_view / get_powerup_selections
    let data = {};
    let selections = [];
    const game = await getCompanyGame(company);
    const companyRosters = await getCompanyRosters(company);
    const mainRoster = await getCompanyRosterByType(company, 'main', companyRosters);
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const trainingRoster = await getCompanyRosterByType(company, 'training', companyRosters);
    const trainingAvailableRoster = await getCompanyRosterByType(company, 'trainingAvailable', companyRosters);
    const companyChampionships = await getCompanyChampionships(company);
    const event = await getCompanyLatestEvent(company);

    const championshipSuperstarIds = companyChampionships.map(x => x.superstarId);
    const superstarsWithPastGimmicks = [];
    for (const s of rosterSuperstars) {
        const gimmicks = await getSuperstarGimmicks(s);
        if (gimmicks.length > 1) {
            superstarsWithPastGimmicks.push(s)
        }
    }
    let trainingSuperstars = [];
    if (trainingRoster) {
        trainingSuperstars.push(...(await getRosterSuperstars(trainingRoster)))
    }
    if (trainingAvailableRoster) {
        trainingSuperstars.push(...(await getRosterSuperstars(trainingAvailableRoster)))
    }

    // Check if stock available
    if (powerup.stock <= 0) {
        data['error'] = 'stock-empty';
        return data;
    }

    // Check for already active powerups
    const brandEffects = ['allow_other_brands', 'allow_other_brands_extended'];
    const otherEffects = ['increase_matches', 'raise_multiplier'];
    if (brandEffects.concat(otherEffects).indexOf(powerup.effect) >= 0) {
        // Show brand artifacts as active if any of them has been used
        if (event.activeEffect === powerup.effect ||
            (brandEffects.indexOf(event.activeEffect) >= 0 && brandEffects.indexOf(powerup.effect) >= 0)) {
            data['error'] = 'upcoming-show-active';
            return data;
        }
    }

    function getPowerupSelectionSuperstarsExceptChampions() {
        return getPowerupSelectionSuperstars(rosterSuperstars, true)
    }

    function getPowerupSelectionPastGimmickSuperstars() {
        return getPowerupSelectionSuperstars(rosterSuperstars, false, true, true)
    }

    function getPowerupSelectionAllSuperstars() {
        return getPowerupSelectionSuperstars(rosterSuperstars, false, false)
    }

    function getPowerupSelectionTrainingSuperstars() {
        return getPowerupSelectionSuperstars(trainingSuperstars, false, false)
    }

    function getPowerupSelectionSuperstars(superstars, filterChampions = false, filterRetired = true,
                                           filterPastGimmicks = false) {
        if (!superstars) {
            superstars = rosterSuperstars
        }
        let selectedSuperstars = superstars.filter(x => x.activeEffect !== 'traded' && !x.dead);
        if (filterRetired) {
            selectedSuperstars = selectedSuperstars.filter(x => !x.retired)
        }
        if (filterChampions) {
            selectedSuperstars = selectedSuperstars.filter(x => championshipSuperstarIds.indexOf(x.$loki) < 0)
        }
        if (filterPastGimmicks) {
            selectedSuperstars = selectedSuperstars.filter(x => objectInArray(x, superstarsWithPastGimmicks))
        }
        return selectedSuperstars.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    function getPowerupSelectionInjuredSuperstars(injuredLimit = 100) {
        let selectedSuperstars = rosterSuperstars.filter(x => x.activeEffect !== 'traded' && !x.dead && x.injured > 0
            && x.injured <= injuredLimit);
        return selectedSuperstars.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    function getPowerupSelectionReturningSuperstars() {
        return getPowerupSelectionInjuredSuperstars(4)
    }

    function getPowerupSelectionRetiredSuperstars() {
        let selectedSuperstars = rosterSuperstars.filter(x => x.activeEffect !== 'traded' && !x.dead
            && !x.fullTimeManager && x.retired);
        if (powerup.level === 1) {
            selectedSuperstars = selectedSuperstars.filter(x => !x.hallOfFamer)
        }
        else if (powerup.level === 2) {
            selectedSuperstars = selectedSuperstars.filter(x => x.hallOfFamer)
        }
        return selectedSuperstars.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    function getPowerupSelectionUnhappySuperstars() {
        let selectedSuperstars = rosterSuperstars.filter(x => x.activeEffect !== 'traded' && !x.dead &&
            (x.unhappy || x.contractMonthsRemaining !== null));
        return selectedSuperstars.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    function getPowerupSelectionPartTimeSuperstars() {
        let selectedSuperstars = rosterSuperstars.filter(x => x.activeEffect !== 'traded' && !x.dead &&
            !x.fullTimeManager && x.partTimer && !x.retired && x.activeEffect !== 'retirement_return');
        return selectedSuperstars.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    function getPowerupSelectionDeadSuperstars() {
        let selectedSuperstars = rosterSuperstars.filter(x => x.activeEffect !== 'traded' && x.dead);
        return selectedSuperstars.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    async function getPowerupSelectionCompetitors() {
        let competitors = await getCpuControlledCompanies(company, game);
        return competitors.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    async function getPowerupSelectionObjectives() {
        let objectives = (await getCompanyObjectives(company)).filter(x => !x.past && !x.failed && !x.completed);
        return objectives.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        })
    }

    async function getPowerupSelectionMatches() {
        let matches = await getEventMatches(event);
        matches.sort((a, b) => b.order - a.order);
        return Promise.all(matches.map(async x => {
            const participants = await getMatchParticipants(x);
            return {'name': participants.map(y => y.name).join(' vs '), 'uid': x.$loki}
        }))
    }

    async function getPowerupSelectionTrainingFactions() {
        // Add option to select random superstars
        let returnData = [{'name': 'Random', 'uid': ''}];
        let factions = await getRosterActiveFactions(trainingRoster);
        returnData.push(...factions.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        }));
        return returnData;
    }

    async function getPowerupSelectionOwnerAndManagers() {
        // Add option to select random superstars
        let returnData = [{'name': 'Owner', 'uid': ''}];
        let managers = rosterSuperstars.filter(x => x.fullTimeManager && !x.activeEffect);
        returnData.push(...managers.map(x => {
            return {'name': x.name, 'uid': x.$loki}
        }));
        return returnData;
    }

    const checkFunctions = {
        'objectives': getPowerupSelectionObjectives,
        'training_superstars': getPowerupSelectionTrainingSuperstars,
        'part_time_superstars': getPowerupSelectionPartTimeSuperstars,
        'returning_superstars': getPowerupSelectionReturningSuperstars,
        'training_factions': getPowerupSelectionTrainingFactions,
        'owner_and_managers': getPowerupSelectionOwnerAndManagers,
        'superstars_except_champions': getPowerupSelectionSuperstarsExceptChampions,
        'past_gimmick_superstars': getPowerupSelectionPastGimmickSuperstars,
        'matches': getPowerupSelectionMatches,
        'injured_superstars': getPowerupSelectionInjuredSuperstars,
        'competitors': getPowerupSelectionCompetitors,
        'unhappy_superstars': getPowerupSelectionUnhappySuperstars,
        'retired_superstars': getPowerupSelectionRetiredSuperstars,
        'dead_superstars': getPowerupSelectionDeadSuperstars,
        'all_superstars': getPowerupSelectionAllSuperstars,
        'superstars': getPowerupSelectionSuperstars
    };

    // Get selections
    if (powerup.selection) {
        selections = await checkFunctions[powerup.selection]();
        if (selections.length <= 0) {
            data['error'] = 'not-available';
            data['selectionsType'] = powerup.selection;
            return data;
        }
    }

    data['selections'] = selections;
    data['uid'] = powerup.$loki;

    return data;
}

async function applyPowerupToSelection(company, powerup, selectionUid = null) {
    let data = {};
    const game = await getCompanyGame(company);
    const mainCompany = await getGameMainUserCompany(game);
    const companyRosters = await getCompanyRosters(company);
    const mainRoster = await getCompanyRosterByType(company, 'main', companyRosters);
    const trainingRoster = await getCompanyRosterByType(company, 'training', companyRosters);
    const trainingAvailableRoster = await getCompanyRosterByType(company, 'trainingAvailable', companyRosters);
    const companyChampionships = await getCompanyChampionships(company);

    // Check if stock available
    if (powerup.stock <= 0) {
        data['error'] = 'stock-empty';
        return data;
    }

    async function applyPowerupIncreaseMatches() {
        const event = await getCompanyLatestEvent(company);
        if (event.activeEffect) {
            data['error'] = 'cannot-use-on-event';
            return null
        }

        // Set active effect
        event.activeEffect = 'increase_matches';

        // Get maximum matches and segments
        const productionFacility = (await getCompanyFacilities(company)).find(x => x.name === 'Production');
        const parentEvents = await getCompanyParentEvents(company);
        const maximumMatchesAndSegments = getEventMaximumMatchesAndSegments(event, parentEvents, productionFacility);
        return `${event.name} has been upgraded to a Mega Show with up to ${maximumMatchesAndSegments} Matches and Segments!`;
    }

    async function applyPowerupAllowOtherBrands(level = 1) {
        const event = await getCompanyLatestEvent(company);
        if (event.activeEffect) {
            data['error'] = 'cannot-use-on-event';
            return null
        }
        if (!event.brandName) {
            data['error'] = 'event-already-non-exclusive';
            return null
        }

        // Set active effect
        event.activeEffect = level === 1 ? 'allow_other_brands' : 'allow_other_brands_extended';

        if (level === 1) {
            return `Superstars from all brands are available to book at ${event.name}!`
        }
        else {
            return `Superstars from all brands are available to book on all shows up to the next monthly event!`
        }
    }

    async function applyPowerupAllowOtherBrandsExtended() {
        return applyPowerupAllowOtherBrands(2)
    }

    async function applyPowerupPartTimerAppearances() {
        let text;
        const superstar = await Superstars.get(selectionUid);
        if (powerup.level === 1) {
            let appearances = getRandomInt(3, 9);
            superstar.appearances += appearances;
            text = `${superstar.name} signed on for ${appearances} more appearances this year!`;

        }
        else if (powerup.level === 2) {
            superstar.appearances = null;
            superstar.partTimer = false;
            text = `${superstar.name} signed on to a full-time deal!`;

            // Add message
            if (company.userControlled) {
                const newMessage = await createResignPowerupMessage(superstar, game.monthCount, game.year);
                await addCompanyMessages(company, [newMessage]);
            }
        }

        return text;
    }

    async function applyPowerupEarlyReturn() {
        const superstar = await Superstars.get(selectionUid);
        superstar.injured = 0;
        endSuperstarInjury(superstar);
        let text = `${superstar.name} has been cleared to immediately return to the ring!`;

        // Add news
        if (game.competitorsPresent) {
            const newsItem = await generateNewsItem({company: company, month: game.monthCount, year: game.year,
                newsType: 'injury_return_powerup', superstar: superstar});
             await addCompanyNewsItems(mainCompany, [newsItem]);
        }

        // Add message
        if (company.userControlled && getRandomInt(0, 100) <= 20) {
            const companyStaffMembers = await getCompanyStaff(company);
            const doctor = getRandomStaffMemberByRole(companyStaffMembers, 'doctor');
            const newMessage = await createInjuryReturnPowerupMessage(superstar, game.monthCount, game.year, doctor);
            await addCompanyMessages(company, [newMessage]);
        }

        return text;
    }

    async function applyPowerupCallup() {
        let text;

        if (powerup.level === 1) {
            const superstar = await Superstars.get(selectionUid);

            // Transfer to main roster and process
            let transferred = await transferTalentToRoster(superstar, mainRoster);
            for (const s of transferred) {
                s.activeEffect = null;
                await removeSuperstarFactionRelationships(s, game.monthCount, game.year);
                await removeSuperstarManagerRelationships(s);
            }

            const superstarTagTeams = await getSuperstarTagTeams(superstar);
            const superstarTeam = superstarTagTeams.length > 0 ? superstarTagTeams[0] : null;
            const callupName = superstarTeam ? superstarTeam.name : superstar.name;
            const verbText = superstarTeam ? 'have' : 'has';
            text = `${callupName} ${verbText} been called up immediately to the Main Roster!`;

            // Add company log
            const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
            await CompanyLogs.insert(newCompanyLog);
            newCompanyLog.text = callupName;
            newCompanyLog.type = 'callup';
            newCompanyLog.realYear = game.year;
            await addCompanyCompanyLogs(company, [newCompanyLog]);
        }
        else if (powerup.level === 2) {
            // Faction
            if (selectionUid) {
                const faction = await Factions.get(selectionUid);
                const factionSuperstars = await getFactionSuperstars(faction);

                // Transfer faction superstars
                for (const x of factionSuperstars) {
                    let transferred = await transferTalentToRoster(x, mainRoster);
                    for (const s of transferred) {
                        s.activeEffect = null;
                        await removeSuperstarManagerRelationships(s);
                    }

                    // Add company log
                    const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
                    await CompanyLogs.insert(newCompanyLog);
                    newCompanyLog.text = x.name;
                    newCompanyLog.type = 'callup';
                    newCompanyLog.realYear = game.year;
                    await addCompanyCompanyLogs(company, [newCompanyLog]);
                }

                // Transfer faction
                await addRosterFactions(mainRoster, [faction]);

                // Add company log
                const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
                await CompanyLogs.insert(newCompanyLog);
                newCompanyLog.text = faction.name;
                newCompanyLog.type = 'callup';
                newCompanyLog.realYear = game.year;
                await addCompanyCompanyLogs(company, [newCompanyLog]);

                text = `${faction.name} have been immediately called up to the Main Roster!`;
            }
            // Random superstars
            else {
                let superstarsToMove = [];
                let teamFound = false;
                let selections = (await getRosterSuperstars(trainingRoster)).concat((await getRosterSuperstars(trainingAvailableRoster)));
                for (const x of Array(getRandomInt(3, 5)).keys()) {
                    if (selections.length > 0) {
                        let superstarToMove = getRandomChoice(selections);

                        // Transfer superstar
                        let transferred = await transferTalentToRoster(superstarToMove, mainRoster);
                        for (const s of transferred) {
                            s.activeEffect = null;
                            await removeSuperstarFactionRelationships(s, game.monthCount, game.year);
                            await removeSuperstarManagerRelationships(s);
                            if (objectInArray(s, selections)) {
                                selections = selections.filter(x => x.$loki !== s.$loki);
                            }
                        }

                        const superstarTagTeams = await getSuperstarTagTeams(superstarToMove);
                        const superstarTeam = superstarTagTeams.length > 0 ? superstarTagTeams[0] : null;
                        if (superstarTeam) {
                            teamFound = true
                        }
                        const callupName = superstarTeam ? superstarTeam.name : superstarToMove.name;
                        superstarsToMove.push(callupName);

                        // Add company log
                        const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
                        await CompanyLogs.insert(newCompanyLog);
                        newCompanyLog.text = callupName;
                        newCompanyLog.type = 'callup';
                        newCompanyLog.realYear = game.year;
                        await addCompanyCompanyLogs(company, [newCompanyLog]);
                    }
                }

                const verbText = superstarsToMove.length === 1 && !teamFound ? 'has' : 'have';
                text = `${commaSeparate(superstarsToMove)} ${verbText} been called up immediately to the Main Roster!`;
            }
        }

        return text;
    }

    async function applyPowerupRetirementReturn() {
        const superstar = await Superstars.get(selectionUid);
        superstar.retired = false;

        let text;
        if (powerup.level === 1 || powerup.level === 2) {
            // Temporary return
            superstar.partTimer = true;
            superstar.appearances = 1;
            superstar.activeEffect = 'retirement_return';
            text = `${superstar.name} is ready to return to the ring for one last match!`;

            // Add news
            if (game.competitorsPresent) {
                const newsItem = await generateNewsItem({company: company, month: game.monthCount, year: game.year,
                    newsType: 'retirement_return_powerup', superstar: superstar});
                await addCompanyNewsItems(mainCompany, [newsItem]);
            }

            // Add message
            if (company.userControlled) {
                const newMessage = await createRetirementReturnPowerupMessage(superstar, game.monthCount, game.year);
                await addCompanyMessages(company, [newMessage]);
            }
        }
        else {
            // Full return
            superstar.partTimer = false;
            text = `${superstar.name} is ready to make a full-time return to the ring!`;

            // Add news
            if (game.competitorsPresent) {
                const newsItem = await generateNewsItem({
                    company: company, month: game.monthCount, year: game.year,
                    newsType: 'retirement_return_permanent_powerup', superstar: superstar
                });
                await addCompanyNewsItems(mainCompany, [newsItem]);
            }

            // Add message
            if (company.userControlled) {
                const newMessage = await createRetirementReturnPermanentPowerupMessage(superstar, game.monthCount, game.year);
                await addCompanyMessages(company, [newMessage]);
            }
        }

        return text;
    }

    async function applyPowerupOneMatch() {
        let text;

        // Add for manager
        if (selectionUid) {
            const superstar = await Superstars.get(selectionUid);
            superstar.activeEffect = 'one_match_appearance';
            text = `${superstar.name} is available to be booked for a match!`;
        }
        // Add for owner
        else {
            // Check for owner
            if (company.ownerAdded) {
                let owner = (await getRosterSuperstars(mainRoster)).find(x => x.name === company.ownerName);
                if (!owner) {
                    data['error'] = 'owner-not-found';
                    return null;
                }
                owner.appearances = getRandomInt(2, 5);
                text = `${owner.name}, the owner of ${company.name}, is ready to step back into the ring!`;
            }
            // Create new superstar if owner not found
            else {
                const sortedSuperstars = (await getRosterSuperstars(mainRoster)).sort((a, b) => b.popularity - a.popularity);
                let popularity = getRandomChoice(sortedSuperstars.slice(0, 5)).popularity + getRandomInt(-150, 10);
                let owner = await generateSuperstar({popularity: popularity, name: company.ownerName, addGraphic: false,
                    notManager: true});
                owner.age = getRandomInt(50, 60);
                owner.charisma = 3;
                owner.charismaXp = 300;
                owner.overness = getRandomInt(60, 75);
                owner.partTimer = true;
                owner.appearances = getRandomInt(2, 5);
                await addRosterSuperstars(mainRoster, [owner]);
                company.ownerAdded = true;
                company.ownerName = owner.name;
                text = `${owner.name}, the owner of ${company.name}, is ready to step into the ring!`;
            }

            // Add message
            const newMessage = await createOwnerAppearancePowerupMessage(company, game.monthCount, game.year);
            await addCompanyMessages(company, [newMessage]);
        }

        return text;
    }

    async function applyPowerupRerollGoal() {
        const objective = await Objectives.get(selectionUid);
        const monthsRemaining = 12 - game.monthCount;
        const properties = await getObjectiveProperties(objective);

        // Get new objective with same tag but different objective name
        const newObjective = await generateCompanySingleObjective(mainRoster, (await getCompanyGimmickMatches(company)), game.year,
            properties[0].tag, companyChampionships, game, properties[0].name, monthsRemaining);
        await removeCompanyObjectives(company, [objective]);
        await addCompanyObjectives(company, [newObjective]);

        let text = `The owner assigned you a new Objective "${newObjective.name}"!`;

        // Add message
        const newMessage = await createChangeObjectivePowerupMessage(newObjective, company, game.monthCount, game.year);
        await addCompanyMessages(company, [newMessage]);

        return text;
    }

    async function applyStyleChangePowerup() {
        // Change style
        let newStyle;
        if (company.style === 'pure') {
            newStyle = 'entertainment'
        }
        else if (company.style === 'entertainment') {
            newStyle = 'pure'
        }
        company.style = newStyle;

        data['style'] = company.style;
        let text = `The company directors agreed to change the company's style to ${toTitleCase(newStyle)}!`;

        // Add message
        if (company.userControlled) {
            const newMessage = await createStyleChangePowerupMessage(newStyle, company, game.monthCount, game.year);
            await addCompanyMessages(company, [newMessage]);
        }

        return text;
    }

    async function applySendToTrainingPowerup() {
        const superstar = await Superstars.get(selectionUid);

        if ((await getSuperstarChampionships(superstar)).length) {
            data['error'] = 'champion';
            return null;
        }

        let text = `${superstar.name} has been sent to the Training Roster with a special focus on improving ${superstar.possessivePronoun} popularity!`;

        // Remove tag teams so they don't transfer
        await removeSuperstarTagTeams(superstar);

        // Transfer talent to training roster
        let transferred = await transferTalentToRoster(superstar, trainingRoster);

        // Set active effect
        for (let s of transferred) {
            s.activeEffect = 'in_training';
            await removeSuperstarFactionRelationships(s, game.monthCount, game.year);
            await removeSuperstarManagerRelationships(s);
            s.brandName = null;
        }

        return text;
    }

    async function applyRecruitmentPowerup() {
		// Generate 3-5 superstars and add to training roster
        let superstars = [];
		for (let x of Array(getRandomInt(3, 5)).keys()) {
			let newSuperstar = await generateSuperstar();
			superstars.push(newSuperstar);
		}
        await addRosterSuperstars(trainingRoster, superstars);

		let text = `Your Training Center held a tryout. ${commaSeparate(superstars.map(x => x.name))} got selected for the Training Roster!`;

		// Add message
        if (company.userControlled) {
            const topSuperstar = getArrayMaxByProperty(superstars, 'popularity');
            const companyStaffMembers = await getCompanyStaff(company);
            const trainer = getRandomStaffMemberByRole(companyStaffMembers, 'trainer');
            const newMessage = await createRecruitmentPowerupMessage(topSuperstar, trainer, company, game.monthCount, game.year);
            await addCompanyMessages(company, [newMessage]);
        }

		return text;
    }

    async function applyStaffRecruitmentPowerup() {
        // Generate 3-4 staff members and add to indie available roster
        let staffMembers = [];
		for (let x of Array(getRandomInt(3, 4)).keys()) {
			let role = getRandomChoice(staffRoles);
            let newStaffMember = await generateStaffMember(role);
			staffMembers.push(newStaffMember);
		}
        const indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
        await addRosterStaff(indieAvailableRoster, staffMembers);

		let text = `Your company held a job ad. ${commaSeparate(staffMembers.map(x => 
            `${x.name} (${toTitleCase(x.role.replace('_', ' '))})`))} listed themselves for hire on the Staff Hiring Board!`;

		// Add message
        if (company.userControlled) {
            const topStaffMember = getArrayMaxByProperty(staffMembers, 'level');
            const companyStaffMembers = await getCompanyStaff(company);
            const talentManager = getRandomStaffMemberByRole(companyStaffMembers, 'talent_manager');
            const newMessage = await createStaffRecruitmentPowerupMessage(topStaffMember, talentManager, company,
                game.monthCount, game.year);
            await addCompanyMessages(company, [newMessage]);
        }

		return text;
    }

    async function applyGimmickMatchRoulettePowerup() {
        const match = await Matches.get(selectionUid);

        let availableGimmickMatches = (await getCompanyGimmickMatches(company)).filter(x => x.available &&
            !x.cashinContractName && !x.matchContractName && x.rules === null && x.consequence !== 'random_opponent'
            && !(x.special && x.name === 'Femme Fatale'));

        if (!availableGimmickMatches.length) {
            data['error'] = 'no-gimmick-matches-available';
            return null;
        }

        if (await getMatchGimmickMatch(match)) {
            data['error'] = 'gimmick-match-already-present';
            return null;
        }

        match.activeEffect = 'gimmick_match_roulette';

        let matchParticipantNames = (await getMatchParticipants(match)).map(x => x.name).join(' vs ');

        return `A Gimmick Match Roulette will be run on ${matchParticipantNames}!`;
    }

    async function applyRandomPastGimmickPowerup() {
        const superstar = await Superstars.get(selectionUid);

        // Select a random gimmick except the current one
        let gimmicks = await getSuperstarGimmicks(superstar);
        let selectedGimmick = getRandomChoice(gimmicks.slice(0, -1));

        // Create and add gimmick to superstar
        let newGimmick = await createGimmick(superstar, selectedGimmick.name, null, 0, 0, selectedGimmick.characterBonus);
        superstar.debutNewGimmick = true;

        return `${superstar.name} is ready to debut ${superstar.possessivePronoun} old "${newGimmick.name}" Gimmick!`;
    }

    // WoM
    async function applyPowerupVirtual() {

    }

    async function applyPowerupHappy() {
        const superstar = await Superstars.get(selectionUid);
        changeSuperstarMoraleToHappy(superstar);
        return `With a successful drug delivery and emotional override, ${superstar.name} has been rid of negative emotions!`;
    }

    async function applyPowerupRaisePopularity() {

    }

    async function applyPowerupInstantHeal() {
        const superstar = await Superstars.get(selectionUid);
        if (superstar.injured > 0) {
            superstar.injured = 0;
            endSuperstarInjury(superstar);
        }
        let text = `With new, synthetic parts, ${superstar.name} is ready to return immediately to the ring!`;

        if (powerup.level === 2) {
            // TODO test
            superstar.cyborg = true;

            // Add news todo
            const newsItem = await createNewsItem('True Cyborg',
                'In a revolutionary experiment, {0} had all moving parts replaced by synthetic substitutes. ' +
                'The results of this are unforeseen, but experts think {1} will be free of any possible injuries ' +
                'in the future'.format(company.name, superstar.name, superstar.name), game.monthCount,
                game.year, company);
            await addCompanyNewsItems(mainCompany, [newsItem]);

            text = `With all moving parts replaced by synthetics, ${superstar.name} will always be ring ready!`;
        }

        return text;
    }

    async function applyPowerupYoungAge() {

    }

    async function applyPowerupFertilize() {

    }

    async function applyPowerupClone() {

    }

    async function applyPowerupSuperstarJump() {
        // Check roster capacity
        const rosterCapacity = getCompanyRosterCapacity(company, game);
        const rosterSize = (await getRosterSuperstars(mainRoster)).filter(x => !x.retired && !x.dead).length;
        if (rosterSize >= rosterCapacity) {
            data['error'] = 'max-roster-capacity';
            return null;
        }

        // Get superstars to move
        const competitor = await Companies.get(selectionUid);
        const competitorMainRoster = await getCompanyRosterByType(competitor, 'main');
        const selections = [];
        for (const s of (await getRosterSuperstars(competitorMainRoster))) {
            const c = await getSuperstarChampionships(s);
            if (c.length <= 0 && s.activeEffect !== 'traded') {
                selections.push(s)
            }
        }
        if (selections.length <= 0) {
            data['error'] = 'empty-roster';
            return null
        }

        // Transfer random superstar
        const superstarToMove = getRandomChoice(selections);
        await addRosterSuperstars(mainRoster, [superstarToMove]);

        // Reset morale
        changeSuperstarMoraleToHappy(superstarToMove);

        // Remove relationships
        await removeSuperstarTagTeams(superstarToMove);
        await removeSuperstarFactionRelationships(superstarToMove, game.monthCount, game.year);
        await removeSuperstarManagerRelationships(superstarToMove);
        superstarToMove.brandName = null;

        let text = `${superstarToMove.name} decided to suddenly leave ${competitor.name} and jump ship to ${company.name}!`;

        // Add news
        const newsItem = await createNewsItem('Brain Washed',
            `In a surprising turn of events, ${superstarToMove.name} decided to jump ship to ${company.name}. Sources are unsure as to how ` +
            `this exactly happened, but the possibility of a neuron-altering experiment can't be ruled out.`, game.monthCount,
            game.year, company);
        await addCompanyNewsItems(mainCompany, [newsItem]);

        return text;
    }

    async function applyPowerupRaiseMultiplier() {
        const event = await getCompanyLatestEvent(company);
        event.activeEffect = 'raise_multiplier';
        return `${event.name} will now be broadcast to Earth, raising its potential fans gained or lost!`;
    }

    async function applyPowerupResurrect() {
        const superstar = await Superstars.get(selectionUid);
        superstar.dead = false;

        // Add news todo
        const newsItem = await createNewsItem('Back from the dead',
        'In a startling experiment, the scientists at {0} managed to bring {0} back from the dead. ' +
            'Experts are puzzled at the success result of the experiment, and it is currently unknown how well ' +
            '{0} can perform in the ring.'.format(company.name, superstar.name, superstar.name), game.monthCount,
            game.year, company);
        await addCompanyNewsItems(mainCompany, [newsItem]);

        return `We managed to restore all non-functioning systems, bringing ${superstar.name} back to life!`;
    }

    const applyFunctions = {
        'callup': applyPowerupCallup,
        'early_return': applyPowerupEarlyReturn,
        'retirement_return': applyPowerupRetirementReturn,
        'part_timer_appearances': applyPowerupPartTimerAppearances,
        'increase_matches': applyPowerupIncreaseMatches,
        'one_match': applyPowerupOneMatch,
        'reroll_goal': applyPowerupRerollGoal,
        'allow_other_brands': applyPowerupAllowOtherBrands,
        'allow_other_brands_extended': applyPowerupAllowOtherBrandsExtended,
        'superstar_jump': applyPowerupSuperstarJump,
        'happy': applyPowerupHappy,
        'instant_heal': applyPowerupInstantHeal,
        'style_change': applyStyleChangePowerup,
        'send_to_training': applySendToTrainingPowerup,
        'recruitment': applyRecruitmentPowerup,
        'staff_recruitment': applyStaffRecruitmentPowerup,
        'gimmick_match_roulette': applyGimmickMatchRoulettePowerup,
        'random_past_gimmick': applyRandomPastGimmickPowerup,

        // TODO
        'virtual': applyPowerupVirtual,
        'raise_multiplier': applyPowerupRaiseMultiplier,
        'raise_popularity': applyPowerupRaisePopularity,
        'young_age': applyPowerupYoungAge,
        'fertilize': applyPowerupFertilize,
        'clone': applyPowerupClone,
        'resurrect': applyPowerupResurrect,
    };

    // Apply function
    let func = applyFunctions[powerup.effect];
    let result;
    if (func) {
        result = await func();
    }

    if (result) {
        // Don't remove stock if sandbox mode
        if (!company.userControlled || !game.settings['unlock_all_items']) {
            powerup.stock -= 1;
        }
    }

    // Get data to return
    data['stock'] = powerup.stock;
    data['result'] = result;
    data['effect'] = powerup.effect;
    const unreadMessages = (await getCompanyMessages(company)).filter(x => !x.read);
    data['unreadMessages'] = unreadMessages.length;

    // Check second story event TODO
    const womEnded = (await getCompanyStoryChoices(company))
        .find(x => ['Ending A 3A', 'Ending A 3B', 'Ending B 3A', 'Ending B 3B'].includes(x.choice));

    return data;
}

function getPowerupsToUnlock(powerups) {  // get_powerups_to_unlock
    // Find powerup of same effect but one level below, check if unlocked
    let toUnlock = [];

    for (const p of powerups) {
        if (!p.unlocked) {
            // Add level 1 powerups
            if (p.level === 1) {
                toUnlock.push(p)
            }
            // Add higher level powerups if level below is unlocked
            else {
                let sameEffectPowerups = powerups.filter(x => x.effect === p.effect && x.$loki !== p.$loki);
                for (const x of sameEffectPowerups) {
                    if ((x.level === p.level - 1) && x.unlocked) {
                        toUnlock.push(p)
                    }
                }
            }
        }
    }

    return toUnlock;
}

function awardPowerup(powerups, unlock = false, rare = false) {  // award_powerup
    let weights = [];

    // Lower rarity for higher level powerups
    for (const x of powerups) {
        let weight = (1000 / x.rarity) ** 2;
        if (rare) {
            if (x.level > 1) {
                weight *= 4
            }
            else {
                weight /= 3
            }
        }
        weights.push([x, weight]);
    }

    // Use weighted choice using powerup rarity
    let powerup = weightedChoice(weights);

    // Set powerup to unlocked
    if (unlock) {
        powerup.unlocked = true;
    }

    // Increase stock
    powerup.stock += 1;

    return powerup;
}

function getPowerupImage(powerup) {  // get_powerup_image
    let level = powerup.level;

    let image;
    if (powerup.storyName === 'mars') {
        if (level === 1) {
            image = 'experiment_1_icon'
        }
        else if (level === 2) {
            image = 'experiment_2_icon'
        }
        else if (level === 3) {
            image = 'experiment_3_icon'
        }
    }
    else {
        if (level === 1) {
            image = globals.game.powerupLevel1
        }
        else if (level === 2) {
            image = globals.game.powerupLevel2
        }
        else if (level === 3) {
            image = globals.game.powerupLevel3
        }
    }

    if (powerup.storyName !== 'mars') {
        image = '<img alt="Artifact" class="pulse" src="static/www/static/' + image + '.png"/>'
    }
    else {
        image = '<img alt="Experiment" src="static/www/static/' + image + '.png"/>'
    }

    return image;
}

async function introducePowerupsView() {  // introduce_powerups_view
    let data = {};
    let currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);

    if (!currentGame.settings['unlock_all_items']) {
        data['powerupIntroduced'] = await introducePowerups(userCompany);
    }

    // Get data to return
    const unreadMessages = await getCompanyUnreadMessages(userCompany);
    data['unreadMessages'] = unreadMessages.length;

    db.saveDatabase();

    return data;
}

async function introducePowerups(company) {  // introduce_powerups
    if (!company.introducedPowerups) {
        let powerups = await getCompanyPowerups(company);

        if (!powerups.length) {
            return false;
        }

        // Unlock and award a level 1 powerup
        let awardedPowerup;
        if (company.storyName !== 'mars') {
            powerups = powerups.filter(x => x.level === 1 && !x.storyName &&
                !['retired_superstars', 'returning_superstars', 'unhappy_superstars'].includes(x.selection));
            awardedPowerup = awardPowerup(powerups, true);
        }
        // Unlock specific experiment for story todo
        else {
            awardedPowerup = powerups.find(x => x.name === 'Virtual');
            awardedPowerup.unlocked = true;
            awardedPowerup.stock = 1;
        }

        if (company.userControlled) {
            let newMessage = await createIntroducePowerupsMessage(awardedPowerup, company);
            await addCompanyMessages(company, [newMessage]);
        }

        company.introducedPowerups = true;

        return true;
    }
    return false;
}

export {getPowerupsData, buyPowerup, applyPowerup, usePowerup, introducePowerupsView, getPowerupImage, getPowerupsToUnlock,
    awardPowerup, introducePowerups, getPowerupSelections, applyPowerupToSelection};